import { memo, useState } from 'react'
import { Link } from './nav'
import { useSelector } from 'react-redux'
import { RefreshCw, Compass, Route } from 'lucide-react'
import type { RootState } from '../store'
import type { Exploration } from '../data/projectTypes'
import { useCreateExplorationMutation, useGetProjectExplorationsQuery } from '../services/projectDataApi'
import { useGetUsageQuery } from '../services/projectDataApi'
import Pagination from './Pagination'
import TaskEventReplay from './TaskEventReplay'
import RowReplayPreview from './RowReplayPreview'
import SubscriptionGate from './SubscriptionGate'
import ModalPortal from './ModalPortal'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { useAdminMode } from '../hooks/useAdminMode'
import './ExplorationsTab.css'

interface ExplorationsTabProps {
  projectId: string
}

// Card thumbnail dimensions (16:9), shared with journey cards.
const CARD_THUMB_W = 240
const CARD_THUMB_H = 135

function statusClass(status: string): string {
  if (status === 'in-progress') return 'explorations-tab__status--in-progress'
  if (status === 'completed') return 'explorations-tab__status--completed'
  if (status === 'failed') return 'explorations-tab__status--failed'
  // 'blocked' ended on a hard obstacle (an unreadable one-time code, CAPTCHA, app never rendered) — did not succeed,
  // so it reuses the failed styling.
  if (status === 'blocked') return 'explorations-tab__status--failed'
  // 'pending' is an exploration's queued state (created, awaiting a container).
  return 'explorations-tab__status--queued'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

function formatStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Queued',
    queued: 'Queued',
    'in-progress': 'In progress',
    completed: 'Completed',
    failed: 'Failed',
    blocked: 'Blocked',
  }
  return labels[status] ?? status.replace(/-/g, ' ')
}

// Human label for a reported obstacle kind (see report-exploration-obstacle), shown on a blocked
// exploration alongside its free-text detail.
function obstacleLabel(kind: string | null | undefined): string {
  const labels: Record<string, string> = {
    email_code: 'One-time code QA cannot read',
    otp: 'One-time verification code required',
    deployment_protection: 'Deployment protection',
    bot_protection: 'Bot protection',
    captcha: 'CAPTCHA challenge',
    page_load: 'App never loaded',
    auth_wall: 'Sign-in wall could not be passed',
    // A shared access secret is a wall in front of the app, not a sign-in — labelled distinctly so it does
    // not read as a login problem (Bug Report #3207).
    access_secret: 'Shared access code not supplied',
    other: 'Blocked',
  }
  return (kind && labels[kind]) ?? 'Blocked'
}

// Single-exploration view: a full rrweb replay preview (with the "View task page" link at its top)
// and the exploration details below it.
function SingleExploration({ exploration: e, projectId }: { exploration: Exploration; projectId: string }) {
  // The task-detail page (and the link into it, now rendered at the top of the replay player) is
  // admin-only; the player itself gates the link on admin mode.
  const taskHref = taskDetailUrl(projectId, e.id)
  return (
    <div className="explorations-tab__single" data-testid="exploration-single">
      <TaskEventReplay source={{ explorationId: e.id }} taskHref={taskHref} active />

      <div className="explorations-tab__single-info">
        <p className="explorations-tab__single-prompt">{e.prompt}</p>
        {e.budget != null && <p>Run budget: {e.budget} credits · journey creation is limited to this budget.</p>}

        <dl className="explorations-tab__single-meta">
          <div className="explorations-tab__single-meta-item">
            <dt>Status</dt>
            <dd>
              <span className={`explorations-tab__status ${statusClass(e.status)}`}>{formatStatusLabel(e.status)}</span>
            </dd>
          </div>
          {e.status === 'blocked' && (
            <div className="explorations-tab__single-meta-item" data-testid="exploration-obstacle">
              <dt>Blocked by</dt>
              <dd>
                {obstacleLabel(e.blocked_kind)}
                {e.blocked_detail ? <span className="explorations-tab__obstacle-detail"> — {e.blocked_detail}</span> : null}
              </dd>
            </div>
          )}
          <div className="explorations-tab__single-meta-item">
            <dt>Journeys created</dt>
            <dd>{e.journeys_created}</dd>
          </div>
          <div className="explorations-tab__single-meta-item">
            <dt>Journeys updated</dt>
            <dd>{e.journeys_updated ?? 0}</dd>
          </div>
          <div className="explorations-tab__single-meta-item">
            <dt>Created</dt>
            <dd>{formatDate(e.created_at)}</dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export default memo(function ExplorationsTab({ projectId }: ExplorationsTabProps) {
  const [page, setPage] = useState(1)
  const pageSize = 20
  const explorationsQuery = useGetProjectExplorationsQuery({ projectId, page, pageSize })
  const items = explorationsQuery.data?.items ?? []
  const loading = explorationsQuery.isLoading
  const error = explorationsQuery.isError
  const total = explorationsQuery.data?.total ?? 0
  const [createExploration, createExplorationState] = useCreateExplorationMutation()
  const creating = createExplorationState.isLoading
  const createError = createExplorationState.error ? 'Failed to start exploration' : null
  const [showModal, setShowModal] = useState(false)
  const [showGate, setShowGate] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [budget, setBudget] = useState('')

  // Starting a new exploration costs credits, so — like the new-project form — gate the action when
  // the user has none left. Fetch usage on mount so the check is accurate; a billing outage (no data)
  // fails open, consistent with RequireCredits.
  const user = useSelector((state: RootState) => state.auth.user)
  const usageQuery = useGetUsageQuery(undefined, { skip: !user })
  const usage = usageQuery.data
  // Exploration cards link into the admin-only task-detail page; for non-admins the cards render as
  // plain, non-navigating tiles (same content, no link).
  const adminMode = useAdminMode()
  const outOfCredits = !!usage && usage.available_credits <= 0

  const handleNewExploration = () => {
    if (outOfCredits) setShowGate(true)
    else setShowModal(true)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleCreate = async () => {
    if (!prompt.trim()) return
    await createExploration({ project_id: projectId, prompt: prompt.trim(), ...(budget !== '' ? { budget: Number(budget) } : {}) }).unwrap()
    setShowModal(false)
    setPrompt('')
    void explorationsQuery.refetch()
  }

  // Show the loading state until the first fetch for this project resolves. Without this guard,
  // the freshly-mounted tab would paint the empty state ("No explorations yet") for one render —
  // before the mount effect dispatches fetchExplorations — causing a mostly-blank flash.
  if (loading) {
    return <div className="explorations-tab__loading" role="status" aria-live="polite">Loading explorations...</div>
  }

  if (error) {
    return (
      <div className="explorations-tab__error" role="alert">
        <p>Failed to load explorations</p>
        <button type="button" onClick={() => void explorationsQuery.refetch()} data-analytics-event="exploration-list-retry-button">
          <RefreshCw size={14} aria-hidden="true" />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="explorations-tab" data-testid="explorations-tab">
      <div className="explorations-tab__header">
        <button type="button" className="explorations-tab__new-btn" onClick={handleNewExploration} data-analytics-event="exploration-new-button">
          <Compass size={14} aria-hidden="true" />
          New Exploration
        </button>
      </div>

      {items.length === 0 ? (
        <div className="explorations-tab__empty">No explorations yet. Start one to discover new journeys.</div>
      ) : items.length === 1 ? (
        <SingleExploration exploration={items[0]!} projectId={projectId} />
      ) : (
        <>
          <div className="explorations-tab__grid">
            {items.map((e) => {
              const cardBody = (
                <>
                  <div className="explorations-tab__card-media">
                    <RowReplayPreview source={{ explorationId: e.id }} width={CARD_THUMB_W} height={CARD_THUMB_H} fit="cover" />
                  </div>
                  <div className="explorations-tab__card-body">
                    <div className="explorations-tab__card-titlerow">
                      <span className="explorations-tab__card-title" title={e.prompt}>{e.prompt}</span>
                      <span className={`explorations-tab__status ${statusClass(e.status)}`}>
                        {formatStatusLabel(e.status)}
                      </span>
                    </div>
                    <div className="explorations-tab__card-meta">
                      <span className="explorations-tab__card-metaitem">
                        <Route size={12} aria-hidden /> {e.journeys_created} created
                      </span>
                      <span className="explorations-tab__card-date">{formatDate(e.created_at)}</span>
                    </div>
                  </div>
                </>
              )
              return adminMode ? (
                <Link
                  key={e.id}
                  to={taskDetailUrl(projectId, e.id)}
                  className="explorations-tab__card"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`exploration-card-${e.id}`}
                  data-analytics-event="exploration-card-link"
                >
                  {cardBody}
                </Link>
              ) : (
                <div
                  key={e.id}
                  className="explorations-tab__card explorations-tab__card--static"
                  data-testid={`exploration-card-${e.id}`}
                >
                  {cardBody}
                </div>
              )
            })}
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={handlePageChange} />
        </>
      )}

      {showModal && (
        <ModalPortal>
        <div className="explorations-tab__modal-overlay" role="presentation" onClick={() => setShowModal(false)}>
          <div className="explorations-tab__modal" role="dialog" aria-labelledby="explorations-modal-title" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="explorations-modal-title">New Exploration</h2>
            <div className="explorations-tab__modal-field">
              <label>Run budget (credits)
                <input type="number" min="1" step="0.5" value={budget} onChange={event => setBudget(event.target.value)} placeholder="No limit" />
                <span>Includes the exploration and its journeys. Reaching this budget limits journeys without pausing the project.</span>
              </label>
              <label htmlFor="exploration-prompt">What should the agents explore?</label>
              <textarea
                id="exploration-prompt"
                value={prompt}
                onChange={(ev) => setPrompt(ev.target.value)}
                placeholder="e.g. Explore all CRUD operations on the user management pages"
                data-analytics-event="exploration-prompt-input"
              />
            </div>
            <div className="explorations-tab__modal-actions">
              <button type="button" className="explorations-tab__modal-cancel" onClick={() => setShowModal(false)} data-analytics-event="exploration-modal-cancel-button">
                Cancel
              </button>
              <button
                type="button"
                className="explorations-tab__modal-submit"
                onClick={handleCreate}
                disabled={creating || !prompt.trim() || (budget !== '' && (!Number.isFinite(Number(budget)) || Number(budget) < 1))}
                data-analytics-event="exploration-modal-submit"
              >
                <Compass size={14} aria-hidden="true" />
                {creating ? 'Starting...' : 'Start Exploration'}
              </button>
            </div>
            {createError && <div className="explorations-tab__create-error">{createError}</div>}
          </div>
        </div>
        </ModalPortal>
      )}

      {showGate && <SubscriptionGate onClose={() => setShowGate(false)} />}
    </div>
  )
})
